import React, { useState, useEffect } from 'react';
import { Wind, Activity, Edit2, Trash2, Info, User, Plus, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from "sonner";
import { format, parseISO, differenceInDays } from 'date-fns';
import { testsApi } from '../../lib/api/tests';
import { testSchedulesApi, TestSchedule } from '../../lib/api/test-schedules';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';

interface SpirometryData {
  id: number;
  test_date: string;
  fev1_liters: number | null;
  fev1_percent_predicted: number | null;
  fvc_liters: number | null;
  fvc_percent_predicted: number | null;
  fev1_fvc_ratio: number | null;
  pef_liters_per_min: number | null;
  technique_quality: string | null;
  test_location: string | null;
  notes: string | null;
}

interface SixMWTData {
  id: number;
  test_date: string;
  distance_meters: number | null;
  pre_test_spo2: number | null;
  pre_test_hr: number | null;
  post_test_spo2: number | null;
  post_test_hr: number | null;
  lowest_spo2: number | null;
  borg_score: number | null;
  test_location: string | null;
  environmental_conditions: string | null;
  notes: string | null;
}

interface NurseTestsManagementProps {
  /** Single assigned patient (nurses always have only ONE patient) */
  patient?: {
    id: number;
    name: string;
    umr_no: string;
    age?: number;
    gender?: string;
  } | null;
}

export function NurseTestsManagement({ patient }: NurseTestsManagementProps) {
  const [activeTab, setActiveTab] = useState<'spirometry' | 'sixmwt'>('spirometry');
  const [editingSpiro, setEditingSpiro] = useState<SpirometryData | null>(null);
  const [editing6MWT, setEditing6MWT] = useState<SixMWTData | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletingType, setDeletingType] = useState<'spirometry' | 'sixmwt' | null>(null);

  // Record new test modals
  const [recordingSpiro, setRecordingSpiro] = useState(false);
  const [recording6MWT, setRecording6MWT] = useState(false);

  // Edit form states
  const [spiroForm, setSpiroForm] = useState<Partial<SpirometryData>>({});
  const [sixMWTForm, setSixMWTForm] = useState<Partial<SixMWTData>>({});

  // New test recording form states
  const [newSpiroForm, setNewSpiroForm] = useState({
    fev1_liters: '',
    fev1_percent_predicted: '',
    fvc_liters: '',
    fvc_percent_predicted: '',
    fev1_fvc_ratio: '',
    pef_liters_per_min: '',
    technique_quality: '',
    test_location: '',
    notes: ''
  });

  const [new6MWTForm, setNew6MWTForm] = useState({
    distance_meters: '',
    pre_test_spo2: '',
    pre_test_hr: '',
    post_test_spo2: '',
    post_test_hr: '',
    lowest_spo2: '',
    borg_score: '',
    test_location: '',
    environmental_conditions: '',
    notes: ''
  });

  // Fetch data states
  const [spirometryData, setSpirometryData] = useState<any>(null);
  const [sixMWTData, setSixMWTData] = useState<any>(null);
  const [prescribedTests, setPrescribedTests] = useState<TestSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTests = async () => {
    if (!patient?.id) return;

    try {
      setIsLoading(true);
      const [spiroRes, sixMWTRes, schedulesRes] = await Promise.all([
        testsApi.getSpirometryHistory(patient.id, { days: 7 }),
        testsApi.getSixMWTHistory(patient.id, { days: 7 }),
        testSchedulesApi.getPatientSchedules(patient.id, { is_active: true })
      ]);
      setSpirometryData(spiroRes);
      setSixMWTData(sixMWTRes);
      setPrescribedTests(schedulesRes.schedules || []);
    } catch (error) {
      console.error('Failed to fetch tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patient?.id) {
      fetchTests();
    }
  }, [patient?.id]);

  const refetchAll = fetchTests;

  // Check if a test is editable (within 7 days)
  const isEditable = (testDate: string): boolean => {
    try {
      const date = parseISO(testDate);
      const daysDiff = differenceInDays(new Date(), date);
      return daysDiff <= 7;
    } catch {
      return false;
    }
  };

  // Record new Spirometry test
  const handleRecordSpiro = async () => {
    if (!patient?.id) return;

    if (!newSpiroForm.fev1_liters || !newSpiroForm.fev1_percent_predicted) {
      toast.error('FEV1 values are required');
      return;
    }

    try {
      await testsApi.recordSpirometry({
        patient_id: patient.id,
        fev1_liters: parseFloat(newSpiroForm.fev1_liters),
        fev1_percent_predicted: parseFloat(newSpiroForm.fev1_percent_predicted),
        fvc_liters: newSpiroForm.fvc_liters ? parseFloat(newSpiroForm.fvc_liters) : undefined,
        fvc_percent_predicted: newSpiroForm.fvc_percent_predicted ? parseFloat(newSpiroForm.fvc_percent_predicted) : undefined,
        fev1_fvc_ratio: newSpiroForm.fev1_fvc_ratio ? parseFloat(newSpiroForm.fev1_fvc_ratio) : undefined,
        pef_liters_per_min: newSpiroForm.pef_liters_per_min ? parseFloat(newSpiroForm.pef_liters_per_min) : undefined,
        quality_grade: newSpiroForm.technique_quality || undefined,  // ✅ FIXED: Backend expects quality_grade
        test_location: newSpiroForm.test_location || undefined,
        technician_notes: newSpiroForm.notes || undefined  // ✅ FIXED: Backend expects technician_notes
      });

      toast.success('Spirometry test recorded!', {
        description: 'Test results have been saved successfully',
      });

      setRecordingSpiro(false);
      setNewSpiroForm({
        fev1_liters: '',
        fev1_percent_predicted: '',
        fvc_liters: '',
        fvc_percent_predicted: '',
        fev1_fvc_ratio: '',
        pef_liters_per_min: '',
        technique_quality: '',
        test_location: '',
        notes: ''
      });
      refetchAll();
    } catch (error) {
      console.error('Failed to record spirometry:', error);
      toast.error('Failed to record spirometry test');
    }
  };

  // Record new 6MWT test
  const handleRecord6MWT = async () => {
    if (!patient?.id) return;

    if (!new6MWTForm.distance_meters) {
      toast.error('Distance is required');
      return;
    }

    try {
      await testsApi.recordSixMWT({
        patient_id: patient.id,
        distance_meters: parseFloat(new6MWTForm.distance_meters),
        pre_spo2: new6MWTForm.pre_test_spo2 ? parseInt(new6MWTForm.pre_test_spo2) : undefined,  // ✅ FIXED: Backend expects pre_spo2
        pre_heart_rate: new6MWTForm.pre_test_hr ? parseInt(new6MWTForm.pre_test_hr) : undefined,  // ✅ FIXED: Backend expects pre_heart_rate
        post_spo2: new6MWTForm.post_test_spo2 ? parseInt(new6MWTForm.post_test_spo2) : undefined,  // ✅ FIXED: Backend expects post_spo2
        post_heart_rate: new6MWTForm.post_test_hr ? parseInt(new6MWTForm.post_test_hr) : undefined,  // ✅ FIXED: Backend expects post_heart_rate
        lowest_spo2: new6MWTForm.lowest_spo2 ? parseInt(new6MWTForm.lowest_spo2) : undefined,
        dyspnea_score: new6MWTForm.borg_score ? parseInt(new6MWTForm.borg_score) : undefined,  // ✅ FIXED: Backend expects dyspnea_score
        test_location: new6MWTForm.test_location || undefined,
        notes: (new6MWTForm.notes || '') + (new6MWTForm.environmental_conditions ? ` Environmental: ${new6MWTForm.environmental_conditions}` : '')  // ✅ Preserve environmental_conditions in notes
      });

      toast.success('6-Minute Walk Test recorded!', {
        description: 'Test results have been saved successfully',
      });

      setRecording6MWT(false);
      setNew6MWTForm({
        distance_meters: '',
        pre_test_spo2: '',
        pre_test_hr: '',
        post_test_spo2: '',
        post_test_hr: '',
        lowest_spo2: '',
        borg_score: '',
        test_location: '',
        environmental_conditions: '',
        notes: ''
      });
      refetchAll();
    } catch (error) {
      console.error('Failed to record 6MWT:', error);
      toast.error('Failed to record 6-Minute Walk Test');
    }
  };

  // Handle Spirometry Edit
  const handleEditSpiro = (test: SpirometryData) => {
    if (!isEditable(test.test_date)) {
      toast.error('Cannot edit tests older than 7 days');
      return;
    }
    setEditingSpiro(test);
    setSpiroForm(test);
  };

  const handleSaveSpiro = async () => {
    if (!editingSpiro) return;

    try {
      await testsApi.updateSpirometry(editingSpiro.id, {
        fev1_liters: spiroForm.fev1_liters || undefined,
        fev1_percent_predicted: spiroForm.fev1_percent_predicted || undefined,
        fvc_liters: spiroForm.fvc_liters || undefined,
        fvc_percent_predicted: spiroForm.fvc_percent_predicted || undefined,
        fev1_fvc_ratio: spiroForm.fev1_fvc_ratio || undefined,
        pef_liters_per_min: spiroForm.pef_liters_per_min || undefined,
        technique_quality: spiroForm.technique_quality || undefined,
        test_location: spiroForm.test_location || undefined,
        notes: spiroForm.notes || undefined,
      });
      
      toast.success('Spirometry test updated!', {
        description: 'Test results have been updated successfully',
      });
      
      setEditingSpiro(null);
      setSpiroForm({});
      refetchAll();
    } catch (error) {
      console.error('Failed to update spirometry:', error);
      toast.error('Failed to update spirometry test');
    }
  };

  // Handle 6MWT Edit
  const handleEdit6MWT = (test: SixMWTData) => {
    if (!isEditable(test.test_date)) {
      toast.error('Cannot edit tests older than 7 days');
      return;
    }
    setEditing6MWT(test);
    setSixMWTForm(test);
  };

  const handleSave6MWT = async () => {
    if (!editing6MWT) return;

    try {
      await testsApi.updateSixMWT(editing6MWT.id, {
        distance_meters: sixMWTForm.distance_meters || undefined,
        pre_test_spo2: sixMWTForm.pre_test_spo2 || undefined,
        pre_test_hr: sixMWTForm.pre_test_hr || undefined,
        post_test_spo2: sixMWTForm.post_test_spo2 || undefined,
        post_test_hr: sixMWTForm.post_test_hr || undefined,
        lowest_spo2: sixMWTForm.lowest_spo2 || undefined,
        borg_score: sixMWTForm.borg_score || undefined,
        test_location: sixMWTForm.test_location || undefined,
        environmental_conditions: sixMWTForm.environmental_conditions || undefined,
        notes: sixMWTForm.notes || undefined,
      });
      
      toast.success('6-Minute Walk Test updated!', {
        description: 'Test results have been updated successfully',
      });
      
      setEditing6MWT(null);
      setSixMWTForm({});
      refetchAll();
    } catch (error) {
      console.error('Failed to update 6MWT:', error);
      toast.error('Failed to update 6MWT test');
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    if (!deletingId || !deletingType) return;

    try {
      if (deletingType === 'spirometry') {
        await testsApi.deleteSpirometry(deletingId);
        toast.success('Spirometry test deleted');
        refetchAll();
      } else {
        await testsApi.deleteSixMWT(deletingId);
        toast.success('6-Minute Walk Test deleted');
        refetchAll();
      }
      setDeletingId(null);
      setDeletingType(null);
    } catch (error) {
      console.error('Failed to delete test:', error);
      toast.error('Failed to delete test');
    }
  };

  const spirometryTests = spirometryData?.results || [];
  const sixMWTTests = sixMWTData?.results || [];
  
  const spirometrySchedules = prescribedTests.filter(s => s.test_type === 'SPIROMETRY');
  const sixMWTSchedules = prescribedTests.filter(s => s.test_type === 'SIX_MWT');

  // No patient assigned
  if (!patient) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <EmptyState
            title="No patient assigned"
            description="You don't have a patient assigned yet. Contact your coordinator for a patient assignment."
            icon={User}
          />
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading home tests..." />;
  }

  return (
    <div className="space-y-4">
      {/* Patient Info Card */}
      <Card className="shadow-lg border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Assigned Patient</p>
              <p className="text-lg text-primary">{patient.name}</p>
              <p className="text-sm text-muted-foreground">
                UMR: {patient.umr_no}
                {patient.age && patient.gender && ` • ${patient.age}yrs, ${patient.gender}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div>
        <h2 className="text-primary">Patient Home Tests</h2>
        <p className="text-sm text-muted-foreground">Record new tests and manage test results</p>
      </div>

      {/* Prescribed Tests Section */}
      {prescribedTests.length > 0 && (
        <Card className="border-[#14CAF4]/30 bg-[#14CAF4]/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#14CAF4]" />
              Prescribed Tests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {prescribedTests.map((schedule) => (
              <div key={schedule.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                <div className="flex items-center gap-3">
                  {schedule.test_type === 'SPIROMETRY' ? (
                    <Wind className="w-5 h-5 text-primary" />
                  ) : (
                    <Activity className="w-5 h-5 text-accent" />
                  )}
                  <div>
                    <p className="font-medium">
                      {schedule.test_type === 'SPIROMETRY' ? 'Spirometry Test' : '6-Minute Walk Test'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {schedule.frequency} • Prescribed by {schedule.prescribed_by?.name || 'Therapist'}
                    </p>
                    {schedule.instructions && (
                      <p className="text-xs text-muted-foreground italic mt-1">{schedule.instructions}</p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    if (schedule.test_type === 'SPIROMETRY') {
                      setRecordingSpiro(true);
                    } else {
                      setRecording6MWT(true);
                    }
                  }}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Record Result
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Info Alert - 7 Day Policy */}
      <Alert className="border-l-4 border-l-[#14CAF4] bg-[#14CAF4]/5">
        <Info className="h-4 w-4 text-[#14CAF4]" />
        <AlertDescription className="text-foreground">
          You can edit or delete home test results from the last <strong>7 days only</strong>. Older tests are read-only to maintain accurate historical records.
        </AlertDescription>
      </Alert>

      {/* Tabs for Spirometry and 6MWT */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'spirometry' | 'sixmwt')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="spirometry" className="gap-2">
            <Wind className="w-4 h-4" />
            Spirometry ({spirometryTests.length})
          </TabsTrigger>
          <TabsTrigger value="sixmwt" className="gap-2">
            <Activity className="w-4 h-4" />
            6-Minute Walk Test ({sixMWTTests.length})
          </TabsTrigger>
        </TabsList>

        {/* Spirometry Tab */}
        <TabsContent value="spirometry" className="space-y-4 mt-6">
          {/* Record Button */}
          <div className="flex justify-end">
            <Button onClick={() => setRecordingSpiro(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Record Spirometry Test
            </Button>
          </div>

          {spirometryTests.length === 0 ? (
            <EmptyState
              icon={Wind}
              title="No spirometry tests"
              description="No spirometry tests recorded in the last 7 days."
            />
          ) : (
            <div className="space-y-4">
              {spirometryTests.map((test: any) => {
                const editable = isEditable(test.test_date);
                const testDate = parseISO(test.test_date);

                return (
                  <Card key={test.id} className="shadow-md hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Wind className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {format(testDate, 'PPP')}
                              {!editable && (
                                <Badge variant="secondary" className="text-xs">
                                  Read-only
                                </Badge>
                              )}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">
                              {format(testDate, 'p')}
                            </p>
                          </div>
                        </div>
                        {editable && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSpiro(test)}
                              className="gap-1"
                            >
                              <Edit2 className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setDeletingId(test.id);
                                setDeletingType('spirometry');
                              }}
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">FEV1 (L)</p>
                          <p className="font-medium text-primary">{test.fev1_liters?.toFixed(2) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">FEV1 (% pred)</p>
                          <p className="font-medium text-primary">{test.fev1_percent_predicted?.toFixed(0) || 'N/A'}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">FVC (L)</p>
                          <p className="font-medium text-primary">{test.fvc_liters?.toFixed(2) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">FEV1/FVC Ratio</p>
                          <p className="font-medium text-primary">{test.fev1_fvc_ratio?.toFixed(2) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">PEF (L/min)</p>
                          <p className="font-medium text-primary">{test.pef_liters_per_min?.toFixed(0) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Quality</p>
                          <p className="font-medium capitalize">{test.technique_quality || 'N/A'}</p>
                        </div>
                      </div>
                      {test.notes && (
                        <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Notes</p>
                          <p className="text-sm">{test.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* 6MWT Tab */}
        <TabsContent value="sixmwt" className="space-y-4 mt-6">
          {/* Record Button */}
          <div className="flex justify-end">
            <Button onClick={() => setRecording6MWT(true)} className="gap-2 bg-accent hover:bg-accent/90">
              <Plus className="w-4 h-4" />
              Record 6-Minute Walk Test
            </Button>
          </div>

          {sixMWTTests.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="No 6-Minute Walk Tests"
              description="No 6-minute walk tests recorded in the last 7 days."
            />
          ) : (
            <div className="space-y-4">
              {sixMWTTests.map((test: any) => {
                const editable = isEditable(test.test_date);
                const testDate = parseISO(test.test_date);

                return (
                  <Card key={test.id} className="shadow-md hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                            <Activity className="w-5 h-5 text-accent" />
                          </div>
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {format(testDate, 'PPP')}
                              {!editable && (
                                <Badge variant="secondary" className="text-xs">
                                  Read-only
                                </Badge>
                              )}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">
                              {format(testDate, 'p')}
                            </p>
                          </div>
                        </div>
                        {editable && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit6MWT(test)}
                              className="gap-1"
                            >
                              <Edit2 className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setDeletingId(test.id);
                                setDeletingType('sixmwt');
                              }}
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Distance</p>
                          <p className="font-medium text-accent">{test.distance_meters?.toFixed(0) || 'N/A'}m</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Pre SpO₂</p>
                          <p className="font-medium">{test.pre_test_spo2 || 'N/A'}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Post SpO₂</p>
                          <p className="font-medium">{test.post_test_spo2 || 'N/A'}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Pre HR</p>
                          <p className="font-medium">{test.pre_test_hr || 'N/A'} bpm</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Post HR</p>
                          <p className="font-medium">{test.post_test_hr || 'N/A'} bpm</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Borg Score</p>
                          <p className="font-medium">{test.borg_score || 'N/A'}/10</p>
                        </div>
                      </div>
                      {test.notes && (
                        <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Notes</p>
                          <p className="text-sm">{test.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Record New Spirometry Modal */}
      <Dialog open={recordingSpiro} onOpenChange={setRecordingSpiro}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wind className="w-5 h-5 text-primary" />
              Record Spirometry Test
            </DialogTitle>
            <DialogDescription>
              Record new spirometry test results for {patient?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="new_fev1_liters">FEV1 (Liters) *</Label>
              <Input
                id="new_fev1_liters"
                type="number"
                step="0.01"
                value={newSpiroForm.fev1_liters}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, fev1_liters: e.target.value })}
                placeholder="2.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="new_fev1_percent">FEV1 (% Predicted) *</Label>
              <Input
                id="new_fev1_percent"
                type="number"
                step="0.1"
                value={newSpiroForm.fev1_percent_predicted}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, fev1_percent_predicted: e.target.value })}
                placeholder="85"
                required
              />
            </div>
            <div>
              <Label htmlFor="new_fvc_liters">FVC (Liters)</Label>
              <Input
                id="new_fvc_liters"
                type="number"
                step="0.01"
                value={newSpiroForm.fvc_liters}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, fvc_liters: e.target.value })}
                placeholder="3.2"
              />
            </div>
            <div>
              <Label htmlFor="new_fvc_percent">FVC (% Predicted)</Label>
              <Input
                id="new_fvc_percent"
                type="number"
                step="0.1"
                value={newSpiroForm.fvc_percent_predicted}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, fvc_percent_predicted: e.target.value })}
                placeholder="90"
              />
            </div>
            <div>
              <Label htmlFor="new_fev1_fvc">FEV1/FVC Ratio</Label>
              <Input
                id="new_fev1_fvc"
                type="number"
                step="0.01"
                value={newSpiroForm.fev1_fvc_ratio}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, fev1_fvc_ratio: e.target.value })}
                placeholder="0.78"
              />
            </div>
            <div>
              <Label htmlFor="new_pef">PEF (L/min)</Label>
              <Input
                id="new_pef"
                type="number"
                step="1"
                value={newSpiroForm.pef_liters_per_min}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, pef_liters_per_min: e.target.value })}
                placeholder="450"
              />
            </div>
            <div>
              <Label htmlFor="new_quality">Technique Quality</Label>
              <Input
                id="new_quality"
                value={newSpiroForm.technique_quality}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, technique_quality: e.target.value })}
                placeholder="good, fair, poor"
              />
            </div>
            <div>
              <Label htmlFor="new_location">Test Location</Label>
              <Input
                id="new_location"
                value={newSpiroForm.test_location}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, test_location: e.target.value })}
                placeholder="Home, Clinic, etc."
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="new_notes">Notes</Label>
              <Textarea
                id="new_notes"
                value={newSpiroForm.notes}
                onChange={(e) => setNewSpiroForm({ ...newSpiroForm, notes: e.target.value })}
                rows={3}
                placeholder="Any observations..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordingSpiro(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordSpiro}
              className="bg-primary hover:bg-primary/90"
              disabled={!newSpiroForm.fev1_liters || !newSpiroForm.fev1_percent_predicted}
            >
              Record Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record New 6MWT Modal */}
      <Dialog open={recording6MWT} onOpenChange={setRecording6MWT}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent" />
              Record 6-Minute Walk Test
            </DialogTitle>
            <DialogDescription>
              Record new 6-minute walk test results for {patient?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label htmlFor="new_distance">Distance (meters) *</Label>
              <Input
                id="new_distance"
                type="number"
                step="1"
                value={new6MWTForm.distance_meters}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, distance_meters: e.target.value })}
                placeholder="400"
                required
              />
            </div>
            <div>
              <Label htmlFor="new_pre_spo2">Pre-Test SpO₂ (%)</Label>
              <Input
                id="new_pre_spo2"
                type="number"
                min="0"
                max="100"
                value={new6MWTForm.pre_test_spo2}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, pre_test_spo2: e.target.value })}
                placeholder="96"
              />
            </div>
            <div>
              <Label htmlFor="new_post_spo2">Post-Test SpO₂ (%)</Label>
              <Input
                id="new_post_spo2"
                type="number"
                min="0"
                max="100"
                value={new6MWTForm.post_test_spo2}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, post_test_spo2: e.target.value })}
                placeholder="92"
              />
            </div>
            <div>
              <Label htmlFor="new_pre_hr">Pre-Test Heart Rate (bpm)</Label>
              <Input
                id="new_pre_hr"
                type="number"
                value={new6MWTForm.pre_test_hr}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, pre_test_hr: e.target.value })}
                placeholder="70"
              />
            </div>
            <div>
              <Label htmlFor="new_post_hr">Post-Test Heart Rate (bpm)</Label>
              <Input
                id="new_post_hr"
                type="number"
                value={new6MWTForm.post_test_hr}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, post_test_hr: e.target.value })}
                placeholder="120"
              />
            </div>
            <div>
              <Label htmlFor="new_lowest_spo2">Lowest SpO₂ During Test (%)</Label>
              <Input
                id="new_lowest_spo2"
                type="number"
                min="0"
                max="100"
                value={new6MWTForm.lowest_spo2}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, lowest_spo2: e.target.value })}
                placeholder="90"
              />
            </div>
            <div>
              <Label htmlFor="new_borg">Borg Dyspnea Score (0-10)</Label>
              <Input
                id="new_borg"
                type="number"
                min="0"
                max="10"
                value={new6MWTForm.borg_score}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, borg_score: e.target.value })}
                placeholder="3"
              />
            </div>
            <div>
              <Label htmlFor="new_6mwt_location">Test Location</Label>
              <Input
                id="new_6mwt_location"
                value={new6MWTForm.test_location}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, test_location: e.target.value })}
                placeholder="Home, Clinic, etc."
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="new_env">Environmental Conditions</Label>
              <Input
                id="new_env"
                value={new6MWTForm.environmental_conditions}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, environmental_conditions: e.target.value })}
                placeholder="Indoor, flat surface, room temperature"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="new_6mwt_notes">Notes</Label>
              <Textarea
                id="new_6mwt_notes"
                value={new6MWTForm.notes}
                onChange={(e) => setNew6MWTForm({ ...new6MWTForm, notes: e.target.value })}
                rows={3}
                placeholder="Any observations..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecording6MWT(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecord6MWT}
              className="bg-accent hover:bg-accent/90"
              disabled={!new6MWTForm.distance_meters}
            >
              Record Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Spirometry Modal */}
      <Dialog open={!!editingSpiro} onOpenChange={(open) => !open && setEditingSpiro(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wind className="w-5 h-5 text-primary" />
              Edit Spirometry Test
            </DialogTitle>
            <DialogDescription>
              Update spirometry test results for {patient?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="fev1_liters">FEV1 (Liters)</Label>
              <Input
                id="fev1_liters"
                type="number"
                step="0.01"
                value={spiroForm.fev1_liters || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, fev1_liters: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="fev1_percent">FEV1 (% Predicted)</Label>
              <Input
                id="fev1_percent"
                type="number"
                step="0.1"
                value={spiroForm.fev1_percent_predicted || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, fev1_percent_predicted: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="fvc_liters">FVC (Liters)</Label>
              <Input
                id="fvc_liters"
                type="number"
                step="0.01"
                value={spiroForm.fvc_liters || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, fvc_liters: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="fev1_fvc">FEV1/FVC Ratio</Label>
              <Input
                id="fev1_fvc"
                type="number"
                step="0.01"
                value={spiroForm.fev1_fvc_ratio || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, fev1_fvc_ratio: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="pef">PEF (L/min)</Label>
              <Input
                id="pef"
                type="number"
                step="1"
                value={spiroForm.pef_liters_per_min || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, pef_liters_per_min: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="quality">Technique Quality</Label>
              <Input
                id="quality"
                value={spiroForm.technique_quality || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, technique_quality: e.target.value })}
                placeholder="good, fair, poor"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={spiroForm.notes || ''}
                onChange={(e) => setSpiroForm({ ...spiroForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSpiro(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSpiro}
              className="bg-primary hover:bg-primary/90"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit 6MWT Modal */}
      <Dialog open={!!editing6MWT} onOpenChange={(open) => !open && setEditing6MWT(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent" />
              Edit 6-Minute Walk Test
            </DialogTitle>
            <DialogDescription>
              Update 6-minute walk test results for {patient?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label htmlFor="distance">Distance (meters)</Label>
              <Input
                id="distance"
                type="number"
                step="1"
                value={sixMWTForm.distance_meters || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, distance_meters: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="pre_spo2">Pre-Test SpO₂ (%)</Label>
              <Input
                id="pre_spo2"
                type="number"
                min="0"
                max="100"
                value={sixMWTForm.pre_test_spo2 || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, pre_test_spo2: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="post_spo2">Post-Test SpO₂ (%)</Label>
              <Input
                id="post_spo2"
                type="number"
                min="0"
                max="100"
                value={sixMWTForm.post_test_spo2 || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, post_test_spo2: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="pre_hr">Pre-Test Heart Rate (bpm)</Label>
              <Input
                id="pre_hr"
                type="number"
                value={sixMWTForm.pre_test_hr || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, pre_test_hr: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="post_hr">Post-Test Heart Rate (bpm)</Label>
              <Input
                id="post_hr"
                type="number"
                value={sixMWTForm.post_test_hr || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, post_test_hr: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="borg">Borg Dyspnea Score (0-10)</Label>
              <Input
                id="borg"
                type="number"
                min="0"
                max="10"
                value={sixMWTForm.borg_score || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, borg_score: parseInt(e.target.value) })}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes_6mwt">Notes</Label>
              <Textarea
                id="notes_6mwt"
                value={sixMWTForm.notes || ''}
                onChange={(e) => setSixMWTForm({ ...sixMWTForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing6MWT(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave6MWT}
              className="bg-accent hover:bg-accent/90"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Test Result?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deletingType === 'spirometry' ? 'spirometry' : '6-minute walk'} test result? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}